import React, {useState} from "react";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import Box from "@mui/material/Box";
import Camera, {FACING_MODES} from "react-html5-camera-photo";
import styled from "@emotion/styled";
import Typography from "@mui/material/Typography";
import ButtonBase from "@mui/material/ButtonBase";
import LinearProgress from "@mui/material/LinearProgress";
import useMediaQuery from "@mui/material/useMediaQuery";

import BigButton from "../../components/BigButton";
import SwitchCameraButton from "../../components/SwitchCameraButton";
import CloseCameraButton from "../../components/CloseCameraButton";
import useNotification from "../../hooks/notification";
import {getImage} from "../../utils/images";

export const CameraContainer = styled.div`
  position: fixed;
  display: flex;
  z-index: 9999;
  width: 100%;
  background: #1b262c;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  div {
    width: 100%;
    video {
      display: flex;
      min-width: 100%;
      height: 100%;
    }
  }
  video {
    max-width: 100%;
  }
`;

export default function ImageInput({ label, imageUrl, onUpload, disabled }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { notifyError } = useNotification();
  const [facingMode, setFacingMode] = useState(FACING_MODES.ENVIRONMENT);
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("sm"));
  const defaultHeight = isMobile ? 120 : 200;

  const toggleOpen = () => setOpen(v => !v);

  const onSuccess = () => {
    setLoading(false);
  };

  const onFailure = errors => {
    notifyError(errors);
    setLoading(false);
  };

  const handleTakePhoto = dataUri => {
    setLoading(true);

    fetch(dataUri)
      .then(res => res.blob())
      .then(file => {
        toggleOpen();

        onUpload(file, { onSuccess, onFailure });
      })
      .catch(error => {
        setLoading(false);
        notifyError(error?.message || "Can't upload file");
      });
  };

  const toggleOpenMode = () =>
    setFacingMode(
      facingMode === FACING_MODES.ENVIRONMENT ? FACING_MODES.USER : FACING_MODES.ENVIRONMENT
    );

  if (open) {
    return (
      <CameraContainer>
        <Camera
          isMaxResolution
          idealFacingMode={facingMode}
          isImageMirror={false}
          onTakePhoto={handleTakePhoto}
        />
        <SwitchCameraButton onClick={toggleOpenMode} />
        <CloseCameraButton onClick={toggleOpen} />
      </CameraContainer>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      flexGrow={1}
      height="100%"
      justifyContent="space-between"
    >
      {imageUrl ? (
        <ButtonBase
          disabled={loading || disabled}
          data-id="camera-button"
          style={{ height: defaultHeight }}
          onClick={toggleOpen}
          sx={{ overflow: "hidden", position: "relative" }}
        >
          <img src={getImage(imageUrl, "width", 400)} style={{ width: "100%" }} />
          <Box position="absolute">
            <AddAPhotoIcon fontSize="large" htmlColor="white" />
          </Box>
        </ButtonBase>
      ) : (
        <BigButton
          disabled={disabled}
          data-id="camera-button"
          component="label"
          onClick={toggleOpen}
          height={defaultHeight}
        >
          <AddAPhotoIcon fontSize="large" />
        </BigButton>
      )}
      {loading && <LinearProgress sx={{ mt: 1 }} />}
      <Typography align="center">{label || "Image"}</Typography>
    </Box>
  );
}
